import { DISPLAY_TYPES, MAPS_TO, MAPS_TO_TYPES, type FieldType, type FormField, type MapsTo } from '@re/form-engine';
import { tr } from '../../lib/i18n';

export const TYPE_GROUPS: { label: string; types: FieldType[] }[] = [
  { label: tr('حقول العقار', 'Property fields'), types: ['request_type', 'property_category', 'property_type', 'property_features', 'location', 'map_point'] },
  { label: tr('إدخال', 'Input'), types: ['text', 'textarea', 'number', 'number_range', 'currency', 'phone', 'email', 'date', 'date_range', 'boolean'] },
  { label: tr('اختيارات', 'Choices'), types: ['radio', 'dropdown', 'checkbox_group'] },
  { label: tr('ملفات', 'Files'), types: ['image_upload', 'file_upload'] },
  { label: tr('عرض', 'Display'), types: ['section', 'info'] },
];

export const isDisplay = (t: FieldType) => DISPLAY_TYPES.has(t);
export const hasStaticOptions = (t: FieldType) => t === 'radio' || t === 'dropdown' || t === 'checkbox_group';

/** Next free key like field_3 */
export function nextKey(fields: FormField[], base = 'field'): string {
  let i = fields.length + 1;
  while (fields.some((f) => f.key === `${base}_${i}`)) i++;
  return `${base}_${i}`;
}

export function newField(type: FieldType, fields: FormField[]): FormField {
  const f: FormField = {
    key: nextKey(fields, type === 'section' ? 'section' : 'field'),
    type,
    order: 0,
    label: { ar: '' },
    required: type === 'request_type', // the request type is always mandatory
    mapsTo: null,
    config: {},
    visibleWhen: null,
    requiredWhen: null,
  };
  if (hasStaticOptions(type)) f.config.options = [{ value: 'OPTION_1', label: { ar: 'خيار 1', en: 'Option 1' } }];
  if (type === 'location') Object.assign(f.config, { depth: 'neighborhood', requiredDepth: 'governorate' });
  if (type === 'image_upload') Object.assign(f.config, { maxFiles: 10, maxSizeMb: 10, accept: ['image/jpeg', 'image/png', 'image/webp'] });
  if (type === 'file_upload') Object.assign(f.config, { maxFiles: 5, maxSizeMb: 15, accept: ['application/pdf'] });
  // natural system mapping for property fields
  const auto: Partial<Record<FieldType, MapsTo>> = { request_type: 'request_type', property_category: 'category', property_type: 'property_type', property_features: 'features', location: 'location', map_point: 'coordinates' };
  if (auto[type] && !fields.some((x) => x.mapsTo === auto[type])) f.mapsTo = auto[type]!;
  return f;
}

/** Re-number orders 10, 20, 30 … after drag & drop. */
export const renumber = (fields: FormField[]) => fields.map((f, i) => ({ ...f, order: (i + 1) * 10 }));

/** System columns this field type may fill (excluding ones already used by other fields). */
export function availableMapsTo(field: FormField, fields: FormField[]): MapsTo[] {
  return MAPS_TO.filter((m) => MAPS_TO_TYPES[m].includes(field.type) && !fields.some((x) => x !== field && x.key !== field.key && x.mapsTo === m));
}

/** snake_case key from any text (English letters only). */
export const toKey = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^(\d)/, 'f_$1')
    .slice(0, 63);

/**
 * Add the fields every form must have (spec §6.7) and fix their flags:
 * request_type first + required + unconditional, contact_name (text) and contact_phone (phone) required.
 */
export function withMandatoryFields(fields: FormField[]): FormField[] {
  let out = fields.map((f) => ({ ...f }));
  let rt = out.find((f) => f.type === 'request_type');
  if (!rt) {
    rt = { ...newField('request_type', out), key: out.some((f) => f.key === 'request_type') ? nextKey(out, 'request_type') : 'request_type', label: { ar: 'نوع الطلب', en: 'Request type' }, section: 'basics' };
    out = [rt, ...out];
  }
  Object.assign(rt, { required: true, visibleWhen: null, requiredWhen: null, mapsTo: 'request_type' });
  out = [rt, ...out.filter((f) => f !== rt)];

  const ensure = (mapsTo: MapsTo, type: FieldType, key: string, label: { ar: string; en: string }, config: FormField['config'] = {}) => {
    const existing = out.find((f) => f.mapsTo === mapsTo);
    if (existing) {
      Object.assign(existing, { required: true, visibleWhen: null });
      return;
    }
    const f = newField(type, out);
    Object.assign(f, { key: out.some((x) => x.key === key) ? nextKey(out, key) : key, label, mapsTo, required: true, section: 'contact', config });
    out.push(f);
  };
  ensure('contact_name', 'text', 'contact_name', { ar: 'الاسم', en: 'Name' }, { validation: { minLength: 2, maxLength: 120 } });
  ensure('contact_phone', 'phone', 'contact_phone', { ar: 'رقم الهاتف', en: 'Phone number' });
  return renumber(out);
}

/** Error codes that withMandatoryFields() can fix automatically. */
export const AUTO_FIXABLE = new Set(['REQUEST_TYPE_FIRST', 'REQUEST_TYPE_COUNT', 'REQUEST_TYPE_CONDITIONAL', 'SYSTEM_FIELD_MISSING', 'SYSTEM_FIELD_OPTIONAL']);

/**
 * Same check as the server's publish gate: everything companies read must exist in English too.
 * Runs live so the builder shows missing translations while typing.
 */
export function translationIssues(fields: FormField[]): { level: 'error'; field: string; code: string; message: string }[] {
  const out: { level: 'error'; field: string; code: string; message: string }[] = [];
  const missing = (l?: { ar?: string; en?: string } | null) => !!l?.ar?.trim() && !l.en?.trim();
  for (const f of fields) {
    const name = f.label?.ar?.trim() || f.key;
    if (missing(f.label)) out.push({ level: 'error', field: f.key, code: 'TRANSLATION_MISSING', message: tr(`عنوان الحقل «${name}» ينقصه النص الإنجليزي`, `Field label “${name}” is missing its English text`) });
    const n = (f.config?.options ?? []).filter((o) => missing(o.label)).length;
    if (n) out.push({ level: 'error', field: f.key, code: 'TRANSLATION_MISSING', message: tr(`${n} من خيارات «${name}» بدون نص إنجليزي`, `${n} option(s) in “${name}” have no English text`) });
    if (missing(f.config?.placeholder)) out.push({ level: 'error', field: f.key, code: 'TRANSLATION_MISSING', message: tr(`النص التوضيحي في «${name}» ينقصه الإنجليزي`, `Placeholder in “${name}” is missing its English text`) });
    if (missing(f.config?.help)) out.push({ level: 'error', field: f.key, code: 'TRANSLATION_MISSING', message: tr(`نص المساعدة في «${name}» ينقصه الإنجليزي`, `Help text in “${name}” is missing its English text`) });
  }
  return out;
}
